<script lang="ts" setup>
  import type { GameMode } from '../../../../shared/api/game.ts';
  import type { SpotifyPlaylist } from '../../../../shared/api/playlists.ts';
  import { Spinner } from '../../../../shared/ui/spinner';

  defineProps<{
    error: string;
    loading: boolean;
    playlists: SpotifyPlaylist[];
  }>();
  const mode = defineModel<GameMode>({ required: true });
  const playlist = defineModel<string>('playlist', { required: true });
</script>

<template>
  <fieldset class="host-game-mode-choice">
    <legend>Game mode</legend>

    <div class="modes">
      <label><input v-model="mode" name="mode" type="radio" value="STANDARD" /> Standard</label>
      <label><input v-model="mode" name="mode" type="radio" value="PLAYLIST" /> Playlist</label>
    </div>

    <template v-if="mode === 'PLAYLIST'">
      <div v-if="loading" class="status">
        <Spinner />
        <span>Loading your playlists…</span>
      </div>
      <p v-else-if="error" class="status error" role="alert">{{ error }}</p>
      <template v-else>
        <ul>
          <li v-for="candidate in playlists" :key="candidate.id">
            <label>
              <input v-model="playlist" name="playlist" type="radio" :value="candidate.id" />
              <span class="choice">
                <span class="name">{{ candidate.name }}</span>
                <span class="meta">{{ candidate.owner }}</span>
              </span>
            </label>
          </li>
        </ul>
        <p class="hint">
          To play another's playlist, save a copy of it with "Add to other playlist" in the Spotify app.
        </p>
      </template>
    </template>
  </fieldset>
</template>

<style scoped>
  .host-game-mode-choice {
    border: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 0;
    max-width: var(--control-max-width);
    padding: 0;
    width: 100%;
  }

  legend {
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
    letter-spacing: 0.1em;
    padding: 0;
    text-transform: uppercase;
  }

  .modes {
    display: flex;
    gap: 1.5rem;
  }

  label {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    min-height: var(--size-touch-target);
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .choice {
    display: flex;
    flex-direction: column;
  }

  .meta,
  .hint {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .hint {
    margin: 0;
  }

  .status {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    margin: 0;
  }

  .error {
    color: var(--color-error);
  }
</style>
